/**
 * project-identity.js — "is this still the project I was describing?" (BC-3, F6).
 *
 * Surfaces that cache a fact ABOUT a project (where it is published, how many
 * versions it has) must drop that fact the moment the project underneath them
 * changes. The Publish menu's destination card did not: it was only ever
 * written from inside the publish flow, so opening the menu after switching
 * projects showed the previous project's address, and opening it before any
 * publish called a published project "Not published yet".
 *
 * Two different events count as a change, and both must:
 *
 *  - the `projectId` moved (open another project, sign in on a local draft);
 *  - the whole state was REPLACED under the same id (load a file, restore a
 *    version, Start over). `init()` advances the epoch while resetting the
 *    revision; every ordinary mutation advances both together — so
 *    `epoch - revision` changes on a replacement and on nothing else. That is
 *    the test `identity-region.js` already relies on; this is its one home.
 *
 * The store is passed in rather than imported so the helper is testable
 * against a stub and carries no module-graph weight.
 */

/**
 * @param {{getState: Function, subscribe: Function, getEpoch?: Function, getRevision?: Function}} store
 * @returns {{projectId: string|null, generation: number}}
 */
export function readProjectIdentity(store) {
  const projectId = store.getState()?.projectId || null;
  const epoch = typeof store.getEpoch === 'function' ? store.getEpoch() : 0;
  const revision = typeof store.getRevision === 'function' ? store.getRevision() : 0;
  return { projectId, generation: epoch - revision };
}

/**
 * Call `cb({projectId, previousProjectId, replaced})` whenever the project
 * identity changes. Ordinary edits never call it.
 *
 * @returns {() => void} unsubscribe
 */
export function onProjectIdentityChange(store, cb) {
  let last = readProjectIdentity(store);
  return store.subscribe(() => {
    const next = readProjectIdentity(store);
    if (next.projectId === last.projectId && next.generation === last.generation) return;
    const previousProjectId = last.projectId;
    last = next;
    cb({
      projectId: next.projectId,
      previousProjectId,
      replaced: next.projectId === previousProjectId
    });
  });
}
